import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { withRouter } from 'react-router-dom';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import classNames from 'classnames';
import { MLPhoneFormatUtil, MLCapitalizeStringFilter } from 'ml-portals-util-js';
import metadata from './MLARSSearchResults.metadata.json5';

import styles from '../MLSearchResults/MLSearchResults.module.scss';
import messages from '../MLSearchResults/MLSearchResults.messages';
import { Button } from '@jutro/legacy/components';

import { Link } from '@jutro/router';

const MAX_SEARCH_RESULT_DISPLAY = 10;
const lobNames = {
    'A': 'Auto',
    'H': 'Home',
    'U': 'Umbrella',
    'Y': 'Watercraft'
};

function MLARSSearchResults(props) {
    const { history } = props;
    const {
        searchParam: searchValue,
        legacyPolicies,
        forceReload
    } = props.location.state;

    const translator = useTranslator();
    const rowDisplayLimit = MAX_SEARCH_RESULT_DISPLAY; 
    const searchHeader = (
        <FormattedMessage
            {...messages.foundxCountMatchesForxARSQuery}
            values={{
                countSpan: <span className={styles['search-count']}>{legacyPolicies.length}</span>,
                boldQuery: <b>{searchValue}</b>
            }}
        />
    );

    const getAddressTemplate = useCallback((policy) => {
        if (_.isUndefined(policy.insured.address)) {
            return;
        }

        let addressDisplay = '';
        if (!_.isUndefined(policy.insured.address.addressLine1)) {
            addressDisplay += `${policy.insured.address.addressLine1}\n`;
        }
        if (!_.isUndefined(policy.insured.address.addressLine2)) {
            addressDisplay += `${policy.insured.address.addressLine2}\n`;
        }
        addressDisplay += `${policy.insured.address.city}, ${policy.governingRegionShortName}, ${policy.insured.address.postalCode}`;
        return addressDisplay;
    }, []);

    const getLOBPolicyName = useCallback((policy) => {
        return lobNames[policy.productGroupCode];
    }, []);

    const redirectURL = useCallback((policy) => {
        let redirectUrl = '';
        const itemNumber = policy.productGroupCode + policy.policyID + policy.policySuffixID;
        redirectUrl = policy.redirectUrl.replace('*', itemNumber);
        window.open(redirectUrl, '_blank');
    },[]);

    const redirectURLEvt = useCallback((event, policy) => {
        event.preventDefault();
        redirectURL(policy);
    },[redirectURL]);

    const goToSearch = useCallback(() => {
        history.push(`/search/${searchValue}/${forceReload}`);
    }, [history, searchValue, forceReload]);

    const renderARSNameCell = (item) => {
        return (
            <div className={styles['arsName-col']}>
                <h2 className={classNames(styles['account-name'], "font-weight-bold")}>{MLCapitalizeStringFilter.capitalize(item.insured.fullName)}</h2>
            </div>
        )
    };

    const renderARSAccountHolderCell = (item) => {
        return (
            <>
                <p className={classNames(styles['body-copy'], "light", "font-weight-bold")}>{translator(messages.accountHolder)}</p>
                <p className={classNames(styles['body-copy'], "font-weight-bold")}>{item.insured.fullName}</p>            
            </>          
        )
    };

    const renderARSAddressCell = (item) => {
        return (
            <>
                <p className={classNames(styles['body-copy'], "light", "font-weight-bold")}>{translator(messages.address)}</p>
                <p className={styles['body-copy']}>{getAddressTemplate(item)}</p>           
            </>          
        )
    };

    const renderARSPhoneCell = (item) => {
        return (
            <>
                <p className={classNames(styles['body-copy'], "light", "font-weight-bold")}>{translator(messages.phone)}</p>
                <p className={styles['body-copy']}>{MLPhoneFormatUtil.formatPhone(item.insured.phone)}</p>          
            </>          
        )
    };

    const renderARSLineOfBusinessCell = (item) => {
        return (
            <>
                <p className={classNames(styles['body-copy'], "light", "font-weight-bold")}>{translator(messages.lineOfBusiness)}</p>
                <p className={styles['body-copy']}>
                    <Link href="/" onClick={(event) => redirectURLEvt(event, item)}>
                        {`${getLOBPolicyName(item)} - ${item.policyID}`}
                    </Link>              
                </p>
                <p><span className="ml-pill ml-pill-default">{translator(messages.ARS)}</span></p>
            </>       
        )
    };

    const renderARSSelectCell = (item) => {
        return (
            <div>
                <Button onClick={() => redirectURL(item)} type="outlined">
                    {_.upperCase(translator(messages.select))}
                </Button>
            </div>
        )
    };

    const overrideProps = {
        ARSSearchResultTitle: {
            content: searchHeader
        },
        ARSPolicyDataTable: {
            data: legacyPolicies.slice(0, rowDisplayLimit)
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderARSNameCell,
            renderARSAccountHolderCell,
            renderARSAddressCell,
            renderARSPhoneCell,
            renderARSLineOfBusinessCell,
            renderARSSelectCell,
            goToSearch
        },
        resolveClassNameMap: styles,
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

MLARSSearchResults.propTypes = {   
    history: PropTypes.shape({
        push: PropTypes.func
    }),
    location: PropTypes.shape({
        state: PropTypes.shape({
            legacyPolicies: PropTypes.array,
            searchValue: PropTypes.string,
            forceReload: PropTypes.string
        }),
        pathname: PropTypes.string
    }).isRequired
};

export default withRouter(MLARSSearchResults);
